import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LanguageIcon from '@mui/icons-material/Language';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Swal from 'sweetalert2';
import { formatDate } from '../../utils/formatDate';

const DetalleCliente = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();

  // Customer data
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Billing plans (for dropdowns)
  const [billingPlans, setBillingPlans] = useState([]);

  // Dialogs
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [changeDomainOpen, setChangeDomainOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // Add Service form
  const [newService, setNewService] = useState({
    service_type: '',
    domain: '',
    billing_plan_id: '',
    billing_interval_type: '',
  });
  const [addingService, setAddingService] = useState(false);

  // Change Plan form
  const [newPlanId, setNewPlanId] = useState('');
  const [changingPlan, setChangingPlan] = useState(false);

  // Change Domain form
  const [newDomain, setNewDomain] = useState('');
  const [changingDomain, setChangingDomain] = useState(false);
  const [domainError, setDomainError] = useState('');

  // Fetch customer detail
  const fetchCustomer = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/admin/customers/${customerId}`);
      setCustomer(response.data);
    } catch (err) {
      console.error('Error fetching customer:', err);
      setError('Error al cargar los datos del cliente.');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // Fetch billing plans
  const fetchBillingPlans = useCallback(async () => {
    try {
      const response = await api.get('/admin/billing-plans');
      setBillingPlans(response.data.data || response.data || []);
    } catch (err) {
      console.error('Error fetching billing plans:', err);
    }
  }, []);

  useEffect(() => {
    fetchCustomer();
    fetchBillingPlans();
  }, [fetchCustomer, fetchBillingPlans]);

  // Populate edit form when entering edit mode
  const handleStartEdit = () => {
    if (!customer) return;
    setEditForm({
      customer_name: customer.customer_name || '',
      customer_email: customer.customer_email || '',
      customer_phone: customer.customer_phone || '',
      customer_type: customer.customer_type || '',
      billing_type: customer.customer_billing_details?.[0]?.billing_type || '',
      fiscal_identification: customer.customer_details?.fiscal_identification || '',
      address: customer.customer_details?.billing_address?.address || '',
      city: customer.customer_details?.billing_address?.city || '',
      region: customer.customer_details?.billing_address?.region || '',
      postal_code: customer.customer_details?.billing_address?.postal_code || '',
      country_code: customer.customer_details?.billing_address?.country_code || '',
    });
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditForm({});
  };

  const handleEditChange = (field) => (e) => {
    setEditForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // Save customer updates
  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/admin/customers/${customerId}`, {
        customer_name: editForm.customer_name,
        customer_email: editForm.customer_email,
        customer_phone: editForm.customer_phone,
        customer_type: editForm.customer_type,
        billing_type: editForm.billing_type,
        customer_details: {
          ...customer.customer_details,
          fiscal_identification: editForm.fiscal_identification,
          billing_address: {
            address: editForm.address,
            city: editForm.city,
            region: editForm.region,
            postal_code: editForm.postal_code,
            country_code: editForm.country_code,
          },
        },
      });
      setEditing(false);
      await fetchCustomer();
      Swal.fire({
        icon: 'success',
        title: 'Cliente actualizado',
        text: 'Los datos del cliente se han actualizado correctamente.',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error('Error updating customer:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.error || 'Error al actualizar el cliente.',
      });
    } finally {
      setSaving(false);
    }
  };

  // ---------- Add Service ----------
  const handleOpenAddService = () => {
    setNewService({ service_type: '', domain: '', billing_plan_id: '', billing_interval_type: '' });
    setAddServiceOpen(true);
  };

  const handleAddService = async () => {
    if (!newService.service_type || !newService.domain || !newService.billing_plan_id || !newService.billing_interval_type) {
      Swal.fire({ icon: 'warning', title: 'Campos obligatorios', text: 'Rellena todos los campos.' });
      return;
    }
    try {
      setAddingService(true);
      await api.post(`/admin/services/customers/${customerId}/services`, {
        service_type: newService.service_type,
        domain: newService.domain,
        billing_plan_id: newService.billing_plan_id,
        billing_interval_type: newService.billing_interval_type,
      });
      setAddServiceOpen(false);
      await fetchCustomer();
      Swal.fire({
        icon: 'success',
        title: 'Servicio creado',
        text: 'El servicio se ha creado correctamente.',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error('Error creating service:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.error || 'Error al crear el servicio.',
      });
    } finally {
      setAddingService(false);
    }
  };

  // ---------- Change Plan ----------
  const handleOpenChangePlan = (service) => {
    setSelectedService(service);
    setNewPlanId(service.current_plan?.billing_plan_id || '');
    setChangePlanOpen(true);
  };

  const handleChangePlan = async () => {
    if (!newPlanId) {
      Swal.fire({ icon: 'warning', title: 'Selecciona un plan', text: 'Debes seleccionar un plan.' });
      return;
    }
    try {
      setChangingPlan(true);
      await api.put(`/admin/services/${selectedService.service_id}/plan`, {
        billing_plan_id: newPlanId,
      });
      setChangePlanOpen(false);
      setSelectedService(null);
      await fetchCustomer();
      Swal.fire({
        icon: 'success',
        title: 'Plan actualizado',
        text: 'El plan del servicio se ha actualizado correctamente.',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error('Error changing plan:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.error || 'Error al cambiar el plan.',
      });
    } finally {
      setChangingPlan(false);
    }
  };

  // ---------- Change Domain ----------
  const handleOpenChangeDomain = (service) => {
    setSelectedService(service);
    setNewDomain(service.domain || '');
    setDomainError('');
    setChangeDomainOpen(true);
  };

  const validateDomain = (value) => {
    if (!value.trim()) return 'El dominio es obligatorio.';
    const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    if (!domainRegex.test(value.trim())) return 'Formato de dominio no válido.';
    return '';
  };

  const handleChangeDomain = async () => {
    const validationError = validateDomain(newDomain);
    if (validationError) {
      setDomainError(validationError);
      return;
    }
    try {
      setChangingDomain(true);
      await api.put(`/admin/services/${selectedService.service_id}/domain`, {
        domain: newDomain.trim(),
      });
      setChangeDomainOpen(false);
      setSelectedService(null);
      await fetchCustomer();
      Swal.fire({
        icon: 'success',
        title: 'Dominio actualizado',
        text: 'El dominio del servicio se ha actualizado correctamente.',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error('Error changing domain:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.error || 'Error al cambiar el dominio.',
      });
    } finally {
      setChangingDomain(false);
    }
  };

  // ---------- Cancel Service ----------
  const handleCancelService = async (service) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: `Se cancelará el servicio "${service.service_type}" para el dominio "${service.domain}". Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, cancelar servicio',
      cancelButtonText: 'Cancelar',
    });
    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/services/${service.service_id}`);
        await fetchCustomer();
        Swal.fire({
          icon: 'success',
          title: 'Servicio cancelado',
          text: 'El servicio se ha cancelado correctamente.',
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (err) {
        console.error('Error cancelling service:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.error || 'Error al cancelar el servicio.',
        });
      }
    }
  };

  // ---------- Delete Customer (soft) ----------
  const handleDeleteCustomer = async () => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción dará de baja al cliente. No se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, dar de baja',
      cancelButtonText: 'Cancelar',
    });
    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/customers/${customerId}`);
        Swal.fire({
          icon: 'success',
          title: 'Cliente dado de baja',
          text: 'El cliente se ha dado de baja correctamente.',
          timer: 2000,
          showConfirmButton: false,
        });
        navigate('/admin/clientes');
      } catch (err) {
        console.error('Error deleting customer:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.error || 'Error al dar de baja al cliente.',
        });
      }
    }
  };

  // Helper: get plan name from service's current_plan
  const getPlanName = (service) => {
    if (service.current_plan?.plan_name) return service.current_plan.plan_name;
    if (service.current_plan?.billing_plan_id) return service.current_plan.billing_plan_id;
    return '-';
  };

  // Services array from customer
  const services = customer?.customer_services || [];

  // Free trial info from customer_details
  const freeTrial = customer?.customer_details?.free_trial || null;

  // Section title style
  const sectionTitleSx = {
    fontSize: { xs: '16px', md: '18px' },
    fontWeight: 'bold',
    color: '#000',
    mb: 2,
  };

  // Paper card style
  const paperSx = {
    p: { xs: 2, md: 3 },
    boxShadow: '0px 0px 10px 2px rgba(98, 113, 137, 0.46)',
    borderRadius: 2.5,
    mb: 3,
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: '#0F52BD' }} />
      </Box>
    );
  }

  // Error state
  if (error && !customer) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 10 }}>
        <Typography sx={{ color: '#c62828', fontSize: '16px', mb: 2 }}>{error}</Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/admin/clientes')}
          sx={{ textTransform: 'none', borderRadius: '8px', color: '#0F52BD', borderColor: '#0F52BD' }}
        >
          Volver a Clientes
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100vw',
        px: { xs: 0, md: 2.5 },
        py: { xs: 0, md: 4 },
        pb: 0,
        mr: { xs: -6 },
        pr: { xs: 0 },
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          padding: { xs: '12px 16px', sm: '8px 16px', md: '12px 16px' },
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          backgroundColor: 'white',
          zIndex: 1000,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {/* Left: Back button + Title */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              ml: { xs: '50px', sm: '50px', md: 35.5 },
            }}
          >
            <IconButton
              onClick={() => navigate('/admin/clientes')}
              sx={{ mr: 1, color: '#4F4F4F' }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '16px', sm: '18px', md: '20px', lg: '22px' },
                  fontWeight: 'medium',
                  color: '#000',
                }}
              >
                Detalle del Cliente
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: { xs: '12px', sm: '15px', md: '14px' },
                  color: '#666',
                  mt: { xs: 0, md: 0.3 },
                  display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
                }}
              >
                {customer?.customer_name || 'Cliente'}
              </Typography>
            </Box>
          </Box>

          {/* Right: Edit toggle */}
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1.5, sm: 2 },
              mr: { xs: 1.5, md: 5 },
              alignItems: 'center',
            }}
          >
            {!editing ? (
              <Button
                variant="outlined"
                startIcon={<EditIcon sx={{ fontSize: { sm: '18px', md: '16px' } }} />}
                onClick={handleStartEdit}
                sx={{
                  textTransform: 'none',
                  fontWeight: 'medium',
                  color: '#0F52BD',
                  borderColor: '#0F52BD',
                  fontSize: { sm: '12px', md: '12px' },
                  px: { sm: 1.5, md: 1.5 },
                  py: { sm: 0.8, md: 0.8 },
                  borderRadius: '8px',
                  borderWidth: '2px',
                  display: { xs: 'none', sm: 'flex' },
                }}
              >
                Editar
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'medium',
                    color: '#4F4F4F',
                    borderColor: '#4F4F4F',
                    fontSize: { sm: '12px', md: '12px' },
                    px: { sm: 1.5, md: 1.5 },
                    py: { sm: 0.8, md: 0.8 },
                    borderRadius: '8px',
                    borderWidth: '2px',
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'medium',
                    backgroundColor: '#0F52BD',
                    fontSize: { sm: '12px', md: '12px' },
                    px: { sm: 1.5, md: 1.5 },
                    py: { sm: 0.8, md: 0.8 },
                    borderRadius: '8px',
                    '&:hover': { backgroundColor: '#0a3d8f' },
                  }}
                >
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Spacer for fixed header */}
      <Box
        sx={{
          height: { xs: '65px', sm: '70px', md: '100px', lg: '90px' },
          '@media (max-width: 600px) and (min-resolution: 1.25dppx)': { height: '65px' },
          '@media (max-width: 600px) and (min-resolution: 1.5dppx)': { height: '90px' },
          '@media (min-width: 600px) and (min-resolution: 1.25dppx)': { height: '75px' },
          '@media (min-width: 900px) and (min-resolution: 1.25dppx)': { height: '95px' },
        }}
      />

      {/* Main Content */}
      <Box sx={{ ml: { xs: -6, md: 0 } }}>
        <Box sx={{ paddingLeft: { xs: 6, md: 2 }, paddingRight: { xs: 0, md: 4 } }}>

          {/* Section 1: Customer Data */}
          <Paper elevation={0} sx={paperSx}>
            <Typography sx={sectionTitleSx}>Datos del Cliente</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Nombre"
                  value={editing ? editForm.customer_name : (customer?.customer_name || '')}
                  onChange={editing ? handleEditChange('customer_name') : undefined}
                  InputProps={{ readOnly: !editing }}
                  fullWidth
                  size="small"
                  variant={editing ? 'outlined' : 'filled'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  value={editing ? editForm.customer_email : (customer?.customer_email || '')}
                  onChange={editing ? handleEditChange('customer_email') : undefined}
                  InputProps={{ readOnly: !editing }}
                  fullWidth
                  size="small"
                  variant={editing ? 'outlined' : 'filled'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Teléfono"
                  value={editing ? editForm.customer_phone : (customer?.customer_phone || '')}
                  onChange={editing ? handleEditChange('customer_phone') : undefined}
                  InputProps={{ readOnly: !editing }}
                  fullWidth
                  size="small"
                  variant={editing ? 'outlined' : 'filled'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {editing ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de cliente</InputLabel>
                    <Select
                      value={editForm.customer_type}
                      onChange={handleEditChange('customer_type')}
                      label="Tipo de cliente"
                    >
                      <MenuItem value="Business">Business</MenuItem>
                      <MenuItem value="Consumer">Consumer</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    label="Tipo de cliente"
                    value={customer?.customer_type || ''}
                    InputProps={{ readOnly: true }}
                    fullWidth
                    size="small"
                    variant="filled"
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Dirección"
                  value={editing ? editForm.address : (customer?.customer_details?.billing_address?.address || '')}
                  onChange={editing ? handleEditChange('address') : undefined}
                  InputProps={{ readOnly: !editing }}
                  fullWidth
                  size="small"
                  variant={editing ? 'outlined' : 'filled'}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Ciudad"
                  value={editing ? editForm.city : (customer?.customer_details?.billing_address?.city || '')}
                  onChange={editing ? handleEditChange('city') : undefined}
                  InputProps={{ readOnly: !editing }}
                  fullWidth
                  size="small"
                  variant={editing ? 'outlined' : 'filled'}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Región"
                  value={editing ? editForm.region : (customer?.customer_details?.billing_address?.region || '')}
                  onChange={editing ? handleEditChange('region') : undefined}
                  InputProps={{ readOnly: !editing }}
                  fullWidth
                  size="small"
                  variant={editing ? 'outlined' : 'filled'}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Código Postal"
                  value={editing ? editForm.postal_code : (customer?.customer_details?.billing_address?.postal_code || '')}
                  onChange={editing ? handleEditChange('postal_code') : undefined}
                  InputProps={{ readOnly: !editing }}
                  fullWidth
                  size="small"
                  variant={editing ? 'outlined' : 'filled'}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="País"
                  value={editing ? editForm.country_code : (customer?.customer_details?.billing_address?.country_code || '')}
                  onChange={editing ? handleEditChange('country_code') : undefined}
                  InputProps={{ readOnly: !editing }}
                  fullWidth
                  size="small"
                  variant={editing ? 'outlined' : 'filled'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Identificación Fiscal"
                  value={editing ? editForm.fiscal_identification : (customer?.customer_details?.fiscal_identification || '')}
                  onChange={editing ? handleEditChange('fiscal_identification') : undefined}
                  InputProps={{ readOnly: !editing }}
                  fullWidth
                  size="small"
                  variant={editing ? 'outlined' : 'filled'}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Section 2: Services */}
          <Paper elevation={0} sx={paperSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography sx={sectionTitleSx} style={{ marginBottom: 0 }}>
                Servicios ({services.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAddService}
                sx={{
                  textTransform: 'none',
                  fontWeight: 'medium',
                  backgroundColor: '#0F52BD',
                  fontSize: '12px',
                  px: 1.5,
                  py: 0.8,
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: '#0a3d8f' },
                }}
              >
                Añadir servicio
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {services.length === 0 ? (
              <Typography sx={{ color: '#666', fontSize: '14px', textAlign: 'center', py: 4 }}>
                Este cliente no tiene servicios registrados.
              </Typography>
            ) : (
              <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                {/* Grid Headers - desktop only */}
                <Box
                  sx={{
                    display: { xs: 'none', md: 'grid' },
                    gridTemplateColumns: '0.8fr 1.5fr 1fr 0.8fr 0.7fr 1fr 0.8fr',
                    gap: 2,
                    py: 1,
                    borderBottom: '2px solid #E0E0E0',
                    mb: 1,
                    alignItems: 'center',
                  }}
                >
                  {['Tipo', 'Dominio', 'Plan', 'Intervalo', 'Estado', 'Fecha', 'Acciones'].map((label) => (
                    <Typography
                      key={label}
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: '#000',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </Typography>
                  ))}
                </Box>

                {/* Grid Rows */}
                <Stack spacing={0}>
                  {services.map((service) => (
                    <Box
                      key={service.service_id}
                      sx={{
                        display: { xs: 'block', sm: 'grid' },
                        gridTemplateColumns: { sm: '1fr 1.5fr 0.7fr', md: '0.8fr 1.5fr 1fr 0.8fr 0.7fr 1fr 0.8fr' },
                        gap: 2,
                        py: 2,
                        alignItems: 'center',
                        borderBottom: '1px solid #E0E0E0',
                        transition: 'background-color 0.15s ease',
                        '&:hover': { backgroundColor: '#f5f8ff' },
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      {/* Mobile view - stacked card */}
                      <Box
                        sx={{
                          display: { xs: 'block', sm: 'none' },
                          p: 2,
                          border: '1px solid #F0F0F0',
                          borderRadius: '8px',
                          backgroundColor: '#FAFAFA',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                            {service.service_type || '-'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton size="small" title="Cambiar plan" onClick={() => handleOpenChangePlan(service)} sx={{ color: '#0F52BD' }}>
                              <SwapHorizIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" title="Cambiar dominio" onClick={() => handleOpenChangeDomain(service)} sx={{ color: '#0F52BD' }}>
                              <LanguageIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" title="Cancelar servicio" onClick={() => handleCancelService(service)} sx={{ color: '#c62828' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        <Typography sx={{ fontSize: '14px', color: '#666', mb: 1 }}>
                          {service.domain || '-'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Chip
                            label={service.status ? 'Activo' : 'Inactivo'}
                            size="small"
                            sx={{
                              backgroundColor: service.status ? '#e8f5e9' : '#ffebee',
                              color: service.status ? '#2e7d32' : '#c62828',
                              border: `1px solid ${service.status ? '#4caf50' : '#f44336'}`,
                              fontWeight: 'medium',
                              fontSize: '12px',
                            }}
                          />
                          <Typography sx={{ fontSize: '13px', color: '#666' }}>
                            {getPlanName(service)} &middot; {formatDate(service.creation_date)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Tablet view - 3 columns */}
                      <Box sx={{ display: { xs: 'none', sm: 'flex', md: 'none' }, alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 'medium', fontSize: '14px' }}>
                          {service.service_type || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', sm: 'block', md: 'none' }, overflow: 'hidden' }}>
                        <Typography sx={{ fontSize: '14px', color: '#4F4F4F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {service.domain || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', sm: 'flex', md: 'none' }, justifyContent: 'flex-end' }}>
                        <Chip
                          label={service.status ? 'Activo' : 'Inactivo'}
                          size="small"
                          sx={{
                            backgroundColor: service.status ? '#e8f5e9' : '#ffebee',
                            color: service.status ? '#2e7d32' : '#c62828',
                            border: `1px solid ${service.status ? '#4caf50' : '#f44336'}`,
                            fontWeight: 'medium',
                            fontSize: '12px',
                          }}
                        />
                      </Box>

                      {/* Desktop view - 7 columns */}
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 'medium', fontSize: '14px' }}>
                          {service.service_type || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'block' }, overflow: 'hidden' }}>
                        <Typography sx={{ fontSize: '14px', color: '#4F4F4F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {service.domain || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Typography sx={{ fontSize: '14px', color: '#4F4F4F' }}>
                          {getPlanName(service)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Typography sx={{ fontSize: '14px', color: '#4F4F4F' }}>
                          {service.billing_interval_type || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                        <Chip
                          label={service.status ? 'Activo' : 'Inactivo'}
                          size="small"
                          sx={{
                            backgroundColor: service.status ? '#e8f5e9' : '#ffebee',
                            color: service.status ? '#2e7d32' : '#c62828',
                            border: `1px solid ${service.status ? '#4caf50' : '#f44336'}`,
                            fontWeight: 'medium',
                            fontSize: '12px',
                          }}
                        />
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Typography sx={{ fontSize: '14px', color: '#666' }}>
                          {formatDate(service.creation_date)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5 }}>
                        <IconButton
                          size="small"
                          title="Cambiar plan"
                          onClick={() => handleOpenChangePlan(service)}
                          sx={{ color: '#0F52BD', '&:hover': { backgroundColor: '#e8eef7' } }}
                        >
                          <SwapHorizIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Cambiar dominio"
                          onClick={() => handleOpenChangeDomain(service)}
                          sx={{ color: '#0F52BD', '&:hover': { backgroundColor: '#e8eef7' } }}
                        >
                          <LanguageIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Cancelar servicio"
                          onClick={() => handleCancelService(service)}
                          sx={{ color: '#c62828', '&:hover': { backgroundColor: '#ffebee' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Paper>

          {/* Section 3: Billing Details */}
          <Paper elevation={0} sx={paperSx}>
            <Typography sx={sectionTitleSx}>Datos de Facturación</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Tipo de Billing"
                  value={customer?.customer_billing_details?.[0]?.billing_type || '-'}
                  InputProps={{ readOnly: true }}
                  fullWidth
                  size="small"
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Billing ID"
                  value={customer?.customer_billing_details?.[0]?.billing_id || '-'}
                  InputProps={{ readOnly: true }}
                  fullWidth
                  size="small"
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <Typography sx={{ fontSize: '14px', color: '#4F4F4F', mr: 1 }}>Estado:</Typography>
                  <Chip
                    label={customer?.status ? 'Activo' : 'Inactivo'}
                    size="small"
                    sx={{
                      backgroundColor: customer?.status ? '#e8f5e9' : '#ffebee',
                      color: customer?.status ? '#2e7d32' : '#c62828',
                      border: `1px solid ${customer?.status ? '#4caf50' : '#f44336'}`,
                      fontWeight: 'medium',
                      fontSize: '12px',
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Section 4: Free Trial */}
          {freeTrial && (
            <Paper elevation={0} sx={paperSx}>
              <Typography sx={sectionTitleSx}>Prueba Gratuita</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {Object.entries(freeTrial).map(([key, value]) => (
                  <Grid item xs={12} md={4} key={key}>
                    <TextField
                      label={key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      value={
                        typeof value === 'boolean'
                          ? (value ? 'Sí' : 'No')
                          : (typeof value === 'object'
                            ? JSON.stringify(value)
                            : String(value ?? '-'))
                      }
                      InputProps={{ readOnly: true }}
                      fullWidth
                      size="small"
                      variant="filled"
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Danger Zone */}
          <Paper
            elevation={0}
            sx={{
              ...paperSx,
              border: '1px solid #f44336',
              backgroundColor: '#fff5f5',
            }}
          >
            <Typography sx={{ ...sectionTitleSx, color: '#c62828' }}>Zona de Peligro</Typography>
            <Divider sx={{ mb: 2, borderColor: '#f44336' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography sx={{ fontSize: '14px', color: '#000', fontWeight: 500 }}>
                  Dar de baja al cliente
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#666' }}>
                  Esta acción desactivará al cliente y todos sus servicios. No se puede deshacer.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteCustomer}
                sx={{
                  textTransform: 'none',
                  fontWeight: 'medium',
                  backgroundColor: '#c62828',
                  fontSize: '12px',
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: '#b71c1c' },
                  flexShrink: 0,
                  ml: 2,
                }}
              >
                Dar de baja
              </Button>
            </Box>
          </Paper>

        </Box>
      </Box>

      {/* ========== DIALOGS ========== */}

      {/* Add Service Dialog */}
      <Dialog
        open={addServiceOpen}
        onClose={() => setAddServiceOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#000' }}>Añadir Servicio</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de servicio</InputLabel>
              <Select
                value={newService.service_type}
                onChange={(e) => setNewService((prev) => ({ ...prev, service_type: e.target.value }))}
                label="Tipo de servicio"
              >
                <MenuItem value="Widget">Widget</MenuItem>
                <MenuItem value="Analyser">Analyser</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Dominio"
              value={newService.domain}
              onChange={(e) => setNewService((prev) => ({ ...prev, domain: e.target.value }))}
              fullWidth
              size="small"
              placeholder="ejemplo.com"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Plan</InputLabel>
              <Select
                value={newService.billing_plan_id}
                onChange={(e) => setNewService((prev) => ({ ...prev, billing_plan_id: e.target.value }))}
                label="Plan"
              >
                {billingPlans.map((plan) => (
                  <MenuItem key={plan.billing_plan_id} value={plan.billing_plan_id}>
                    {plan.plan_name || plan.billing_plan_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Intervalo</InputLabel>
              <Select
                value={newService.billing_interval_type}
                onChange={(e) => setNewService((prev) => ({ ...prev, billing_interval_type: e.target.value }))}
                label="Intervalo"
              >
                <MenuItem value="M">Mensual</MenuItem>
                <MenuItem value="Y">Anual</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setAddServiceOpen(false)}
            sx={{ textTransform: 'none', color: '#4F4F4F', borderRadius: '8px' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAddService}
            disabled={addingService}
            sx={{
              textTransform: 'none',
              backgroundColor: '#0F52BD',
              borderRadius: '8px',
              '&:hover': { backgroundColor: '#0a3d8f' },
            }}
          >
            {addingService ? 'Creando...' : 'Crear servicio'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog
        open={changePlanOpen}
        onClose={() => setChangePlanOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#000' }}>Cambiar Plan</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: '14px', color: '#666', mb: 2 }}>
              Servicio: {selectedService?.service_type} - {selectedService?.domain}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Nuevo plan</InputLabel>
              <Select
                value={newPlanId}
                onChange={(e) => setNewPlanId(e.target.value)}
                label="Nuevo plan"
              >
                {billingPlans.map((plan) => (
                  <MenuItem key={plan.billing_plan_id} value={plan.billing_plan_id}>
                    {plan.plan_name || plan.billing_plan_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setChangePlanOpen(false)}
            sx={{ textTransform: 'none', color: '#4F4F4F', borderRadius: '8px' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleChangePlan}
            disabled={changingPlan}
            sx={{
              textTransform: 'none',
              backgroundColor: '#0F52BD',
              borderRadius: '8px',
              '&:hover': { backgroundColor: '#0a3d8f' },
            }}
          >
            {changingPlan ? 'Cambiando...' : 'Cambiar plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Domain Dialog */}
      <Dialog
        open={changeDomainOpen}
        onClose={() => setChangeDomainOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#000' }}>Cambiar Dominio</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: '14px', color: '#666', mb: 2 }}>
              Servicio: {selectedService?.service_type} - {selectedService?.domain}
            </Typography>
            <TextField
              label="Nuevo dominio"
              value={newDomain}
              onChange={(e) => {
                setNewDomain(e.target.value);
                if (domainError) setDomainError('');
              }}
              fullWidth
              size="small"
              placeholder="ejemplo.com"
              error={!!domainError}
              helperText={domainError}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setChangeDomainOpen(false)}
            sx={{ textTransform: 'none', color: '#4F4F4F', borderRadius: '8px' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleChangeDomain}
            disabled={changingDomain}
            sx={{
              textTransform: 'none',
              backgroundColor: '#0F52BD',
              borderRadius: '8px',
              '&:hover': { backgroundColor: '#0a3d8f' },
            }}
          >
            {changingDomain ? 'Cambiando...' : 'Cambiar dominio'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DetalleCliente;
